import { useEffect } from "react";
import { Link } from "react-router-dom";

const SERVICES = [
  {
    icon: "ri-search-eye-line",
    title: "450-Point Pre-Purchase Inspection",
    description:
      "Complete bumper-to-bumper mechanical and physical inspection. Engine, transmission, brakes, suspension, electrical — we lift the vehicle and perform a full test drive.",
    to: "/services/pre-purchase-inspection",
    tag: "Core Service",
    image:
      "https://public.readdy.ai/ai/img_res/3ef8c599e565ed9a70e0ef5a3f5d93d0.jpg",
  },
  {
    icon: "ri-file-text-line",
    title: "Detailed Inspection Report & Photos",
    description:
      "After every inspection you receive a comprehensive written report with photographs, findings, and recommendations — everything documented in one clear summary.",
    to: "/services/pre-purchase-inspection",
    tag: "Included Free",
    image:
      "https://readdy.ai/api/search-image?query=professional%20automotive%20inspection%20report%20document%20with%20photographs%20and%20detailed%20technical%20findings%20on%20dark%20desk%2C%20premium%20quality%20close-up%2C%20dramatic%20side%20lighting%2C%20organized%20professional%20paperwork%2C%20photorealistic&width=600&height=380&seq=svc_v3_card2&orientation=landscape",
  },
  {
    icon: "ri-history-line",
    title: "Free Title & Vehicle History Report",
    description:
      "Every inspection includes a free title search and vehicle history report, plus a complimentary book value appraisal so you negotiate with real data.",
    to: "/services/pre-purchase-inspection",
    tag: "Included Free",
    image:
      "https://readdy.ai/api/search-image?query=vehicle%20title%20and%20registration%20documents%20with%20VIN%20research%20on%20dark%20wooden%20desk%2C%20premium%20dramatic%20lighting%2C%20automotive%20paperwork%20investigation%2C%20close%20composition%2C%20photorealistic%20editorial%20photography&width=600&height=380&seq=svc_v3_card3&orientation=landscape",
  },
  {
    icon: "ri-user-voice-line",
    title: "Personal Inspector Consultation",
    description:
      "Immediately after the inspection, the actual technician who inspected your car calls you personally to review every finding. No call center — your actual inspector.",
    to: "/services/pre-purchase-inspection",
    tag: "Exclusive Feature",
    image:
      "https://readdy.ai/api/search-image?query=confident%20professional%20automotive%20expert%20consultant%20speaking%20on%20phone%20with%20clipboard%20and%20inspection%20notes%2C%20dark%20modern%20office%20environment%2C%20editorial%20style%20professional%20photography%2C%20expert%20demeanor%2C%20premium%20quality&width=600&height=380&seq=svc_v3_card4&orientation=landscape",
  },
];

export default function ServicesOverview() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("lp-visible"); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".lp-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-brand-black py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-8 mb-16">
          <div className="lg:w-1/2">
            <p className="lp-reveal font-sans text-xs tracking-widest uppercase text-brand-gold mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-brand-gold" />
              What&apos;s Included
            </p>
            <h2 className="lp-reveal lp-reveal-delay-1 font-serif text-4xl lg:text-5xl font-semibold text-brand-white leading-tight">
              Complete Pre-Purchase Inspection Services
            </h2>
          </div>
          <div className="lg:w-1/2 lg:pl-10">
            <p className="lp-reveal lp-reveal-delay-2 font-sans text-base text-brand-gray leading-relaxed mb-5">
              One appointment. One thorough inspection. Everything you need to make a confident, informed decision before buying any used car.
            </p>
            <Link
              to="/contact"
              className="lp-reveal lp-reveal-delay-3 inline-flex items-center gap-2 px-7 py-3 bg-brand-gold text-brand-black font-sans font-semibold text-xs tracking-widest uppercase rounded-full hover:bg-brand-gold-light transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule a 450-Point Inspection
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((service, i) => (
            <div
              key={i}
              className={`lp-reveal lp-reveal-delay-${i + 1} group bg-brand-charcoal border border-brand-gold/10 rounded-2xl overflow-hidden hover:border-brand-gold/45 lp-card-glow transition-all duration-400 flex flex-col`}
            >
              {/* Image */}
              <div className="w-full h-44 overflow-hidden lp-img-zoom relative">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/80 via-transparent to-transparent" />
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-brand-gold/15 border border-brand-gold/30 text-brand-gold font-sans text-xs whitespace-nowrap backdrop-blur-sm">
                  {service.tag}
                </span>
              </div>

              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`${service.icon} text-brand-gold text-lg`} />
                  </div>
                  <h3 className="font-serif text-base font-semibold text-brand-white leading-snug">
                    {service.title}
                  </h3>
                </div>

                <p className="font-sans text-sm text-brand-gray leading-relaxed flex-1">
                  {service.description}
                </p>

                <Link
                  to={service.to}
                  className="mt-5 inline-flex items-center gap-1.5 font-sans text-sm text-brand-gold hover:text-brand-gold-light transition-colors group-hover:gap-3"
                >
                  Learn More
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
